/**
 * Progress Transfer — learner-facing transfer request form + request list.
 *
 * Tone: calm, positive, opportunity-led. The learner is in charge. Every
 * copy string is reviewed for "rejected / unwanted" language — replaced
 * with "new match / new start / better fit" framing.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Compass, Plus, X, Sparkles } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REASONS = [
  { value: "moved_area", label: "I've moved area", description: "Your progress comes with you." },
  { value: "instructor_unavailable", label: "My instructor isn't available right now", description: "Keep the momentum with a new local match." },
  { value: "better_location", label: "I want a closer / better local match", description: "Save travel, keep focus on driving." },
  { value: "different_style", label: "I'd like a different teaching style", description: "Everyone learns differently — that's okay." },
  { value: "transmission_change", label: "I want to switch manual / automatic", description: "We'll find an instructor who teaches your gearbox." },
  { value: "personal_preference", label: "Personal preference", description: "No reason needed beyond this." },
];

const TRANSMISSIONS = [
  { value: "manual", label: "Manual" },
  { value: "automatic", label: "Automatic" },
  { value: "either", label: "Either" },
];

const DEFAULT_FORM = {
  reason: "",
  new_postcode: "",
  transmission: "manual",
  availability_note: "",
  teaching_preference: "",
  test_booked: false,
  test_date: "",
  notes: "",
};

function StatusPill({ status }) {
  const map = {
    open: { label: "Looking for matches", tint: "bg-indigo-100 text-indigo-700" },
    matched: { label: "Instructors interested", tint: "bg-emerald-100 text-emerald-700" },
    pending_learner_approval: { label: "Awaiting your approval", tint: "bg-amber-100 text-amber-700" },
    completed: { label: "Transfer complete", tint: "bg-emerald-100 text-emerald-700" },
    cancelled: { label: "Cancelled", tint: "bg-slate-200 text-slate-500" },
  };
  const m = map[status] || { label: status, tint: "bg-slate-200 text-slate-500" };
  return (
    <span
      data-testid={`transfer-status-${status}`}
      className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${m.tint}`}
    >
      {m.label}
    </span>
  );
}

function InterestedAdiList({ reqId, onSelected }) {
  const nav = useNavigate();
  const goBack = useSafeBack();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API_URL}/api/transfer-network/requests/${reqId}/interested`, {
        withCredentials: true,
      })
      .then((r) => { if (alive) setRows(r.data?.interests || []); })
      .catch(() => { if (alive) setRows([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [reqId]);

  const openChat = async (row) => {
    const chatId = `${reqId}:${row.adi_id}`;
    nav(`/learn/transfer-chat/${encodeURIComponent(chatId)}`);
  };

  const selectAdi = async (row) => {
    if (!window.confirm(`Continue with ${row.adi_name || "this instructor"}? The other interested instructors will be politely closed.`)) return;
    try {
      await axios.post(
        `${API_URL}/api/transfer-network/requests/${reqId}/select/${row.adi_id}`,
        {},
        { withCredentials: true },
      );
      toast.success(`Great — we've opened the Transfer Hub with ${row.adi_name || "them"}`);
      if (onSelected) onSelected();
      nav(`/learn/transfer-hub/${reqId}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't select");
    }
  };

  if (loading) return null;
  if (rows.length === 0) return null;

  return (
    <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
        Interested instructors ({rows.length})
      </p>
      {rows.map((r) => (
        <div
          key={r.adi_id}
          data-testid={`interested-adi-${r.adi_id}`}
          className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-xl"
        >
          <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center flex-shrink-0 text-xs font-bold">
            {(r.adi_name || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900 truncate">
              {r.adi_name || "Instructor"}
            </p>
            {r.profile?.rating_average && (
              <p className="text-[11px] text-slate-500">
                {"★".repeat(Math.round(r.profile.rating_average))}{" "}
                {r.profile.rating_average.toFixed(1)}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => openChat(r)}
            data-testid={`chat-with-${r.adi_id}`}
            className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-700 px-2 py-1"
          >
            Chat
          </button>
          <button
            type="button"
            onClick={() => selectAdi(r)}
            data-testid={`select-adi-${r.adi_id}`}
            className="text-[11px] font-bold bg-emerald-600 text-white rounded-lg px-2.5 py-1.5 hover:bg-emerald-700"
          >
            Continue with them
          </button>
        </div>
      ))}
    </div>
  );
}

function RequestCard({ req, onCancel, onApprove, onDecline, onOpenHub, refreshKey }) {
  const reasonLabel = REASONS.find((r) => r.value === req.reason)?.label || req.reason;
  const isPending = req.status === "pending_learner_approval";
  const isMatched = req.status === "matched";
  const isCompleted = req.status === "completed";

  return (
    <div
      data-testid={`transfer-request-card-${req.id}`}
      className="bg-white rounded-2xl border border-slate-200 p-4"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center flex-shrink-0">
          <Compass className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusPill status={req.status} />
            <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">
              {req.new_postcode}
            </span>
            <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">
              {req.transmission}
            </span>
            {req.initiated_by === "current_adi" && (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-violet-100 text-violet-700 rounded-full px-2 py-0.5">
                Suggested by your instructor
              </span>
            )}
          </div>
          <p className="text-sm font-bold text-slate-900 mt-1">{reasonLabel}</p>
          {req.availability_note && (
            <p className="text-xs text-slate-500 mt-1">{req.availability_note}</p>
          )}
          {req.notes && <p className="text-xs text-slate-500 italic mt-1">"{req.notes}"</p>}
          <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-500">
            {!isPending && <span>{req.interest_count || 0} instructors interested</span>}
            {req.test_booked && <span className="text-amber-700 font-semibold">Test booked</span>}
          </div>
        </div>
      </div>

      {/* Pending-approval actions (Phase 7 — ADI initiated) */}
      {isPending && (
        <div
          data-testid={`pending-approval-banner-${req.id}`}
          className="mt-3 pt-3 border-t border-slate-100 bg-amber-50 -mx-4 -mb-4 p-4 rounded-b-2xl"
        >
          <p className="text-xs text-amber-900 leading-relaxed mb-3">
            Your instructor has suggested a new local match. You're in control —
            approve to see options, or decline and nothing changes.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onApprove && onApprove(req)}
              data-testid={`approve-transfer-${req.id}`}
              className="flex-1 text-xs font-bold bg-indigo-600 text-white rounded-lg px-3 py-2 hover:bg-indigo-700"
            >
              Approve and see options
            </button>
            <button
              type="button"
              onClick={() => onDecline && onDecline(req)}
              data-testid={`decline-transfer-${req.id}`}
              className="text-xs font-semibold text-slate-600 hover:text-rose-700 px-3 py-2"
            >
              Decline
            </button>
          </div>
        </div>
      )}

      {/* Interested ADIs list + select / chat (Phases 3-4-5) */}
      {isMatched && !req.selected_adi_id && (
        <InterestedAdiList key={refreshKey} reqId={req.id} onSelected={onCancel} />
      )}

      {(isMatched && req.selected_adi_id) && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <button
            type="button"
            onClick={() => onOpenHub && onOpenHub(req)}
            data-testid={`open-hub-${req.id}`}
            className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 text-white font-bold text-sm rounded-xl px-3 py-2.5 hover:bg-emerald-700"
          >
            Open Transfer Hub →
          </button>
        </div>
      )}

      {isCompleted && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <button
            type="button"
            onClick={() => onOpenHub && onOpenHub(req)}
            data-testid={`view-hub-${req.id}`}
            className="w-full text-xs font-semibold text-emerald-700 hover:text-emerald-800 py-1.5"
          >
            View completed handover →
          </button>
        </div>
      )}

      {["open", "matched"].includes(req.status) && !req.selected_adi_id && (
        <div className="flex items-center justify-end mt-2">
          <button
            type="button"
            onClick={() => onCancel(req)}
            data-testid={`transfer-cancel-${req.id}`}
            className="text-xs font-semibold text-slate-500 hover:text-rose-700 px-3 py-1.5"
          >
            Cancel request
          </button>
        </div>
      )}
    </div>
  );
}

function NewRequestForm({ onSave, onClose }) {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.reason) {
      toast.error("Pick a reason so we can match you well");
      return;
    }
    if (!form.new_postcode.trim()) {
      toast.error("Add your new postcode so we can find local instructors");
      return;
    }
    setSaving(true);
    try {
      await onSave({
        ...form,
        new_postcode: form.new_postcode.trim().toUpperCase(),
        test_date: form.test_date || null,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save — check the fields");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="transfer-request-form"
      className="bg-white rounded-2xl border border-slate-200 p-4 space-y-4"
    >
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-slate-900">Start a Progress Transfer</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Your progress comes with you. We'll only match you with instructors who opt in.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="transfer-form-close"
          className="p-1.5 rounded-md hover:bg-slate-100"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Reason */}
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">
          Why are you moving?
        </p>
        <div className="space-y-1.5">
          {REASONS.map((r) => {
            const active = form.reason === r.value;
            return (
              <button
                key={r.value}
                type="button"
                onClick={() => setForm({ ...form, reason: r.value })}
                data-testid={`transfer-reason-${r.value}`}
                className={`w-full text-left rounded-xl border p-3 transition-colors ${
                  active
                    ? "border-indigo-600 bg-indigo-50"
                    : "border-slate-200 hover:bg-slate-50"
                }`}
              >
                <p className={`text-sm font-semibold ${active ? "text-indigo-900" : "text-slate-900"}`}>
                  {r.label}
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5">{r.description}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Postcode + transmission */}
      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs text-slate-600">
          New postcode
          <input
            type="text"
            value={form.new_postcode}
            onChange={(e) => setForm({ ...form, new_postcode: e.target.value.toUpperCase() })}
            placeholder="e.g. M1 2AB"
            maxLength={12}
            data-testid="transfer-new-postcode"
            className="w-full mt-1 text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
        </label>
        <label className="text-xs text-slate-600">
          Transmission
          <select
            value={form.transmission}
            onChange={(e) => setForm({ ...form, transmission: e.target.value })}
            data-testid="transfer-form-transmission"
            className="w-full mt-1 text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2"
          >
            {TRANSMISSIONS.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </label>
      </div>

      {/* Availability */}
      <label className="block text-xs text-slate-600">
        When are you free?
        <textarea
          value={form.availability_note}
          onChange={(e) => setForm({ ...form, availability_note: e.target.value })}
          maxLength={280}
          rows={2}
          placeholder="Weekday evenings + Saturday mornings"
          data-testid="transfer-form-availability"
          className="w-full mt-1 text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 resize-none"
        />
      </label>

      {/* Teaching preference */}
      <label className="block text-xs text-slate-600">
        Anything about how you like to learn? (optional)
        <input
          type="text"
          value={form.teaching_preference}
          onChange={(e) => setForm({ ...form, teaching_preference: e.target.value })}
          maxLength={280}
          placeholder="Calm and patient, lots of explanation"
          data-testid="transfer-form-teaching"
          className="w-full mt-1 text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2"
        />
      </label>

      {/* Test booked */}
      <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
        <input
          type="checkbox"
          checked={form.test_booked}
          onChange={(e) => setForm({ ...form, test_booked: e.target.checked })}
          data-testid="transfer-test-booked"
          className="w-4 h-4 accent-amber-600"
        />
        <div className="flex-1">
          <p className="text-sm font-semibold text-slate-900">I've already booked my test</p>
          <p className="text-[11px] text-slate-500">We'll prioritise test-ready instructors for you</p>
        </div>
        {form.test_booked && (
          <input
            type="date"
            value={form.test_date || ""}
            onChange={(e) => setForm({ ...form, test_date: e.target.value })}
            data-testid="transfer-test-date"
            className="text-xs bg-white border border-amber-300 rounded-lg px-2 py-1"
          />
        )}
      </div>

      <button
        type="submit"
        disabled={saving}
        data-testid="transfer-submit-btn"
        className="w-full inline-flex items-center justify-center gap-2 bg-indigo-600 text-white font-bold text-sm rounded-xl px-4 py-3 hover:bg-indigo-700 disabled:bg-indigo-300"
      >
        <Sparkles className="w-4 h-4" />
        {saving ? "Looking for matches…" : "Find my new match"}
      </button>
      <p className="text-[11px] text-slate-400 text-center">
        You'll always have final choice. No one is notified without your sign-off.
      </p>
    </form>
  );
}

export default function ProgressTransfer() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/transfer-network/requests/my`,
        { withCredentials: true },
      );
      setRequests(data?.requests || []);
    } catch {
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const hasOpen = useMemo(
    () => requests.some((r) => ["open", "matched"].includes(r.status)),
    [requests],
  );

  const handleCreate = async (payload) => {
    const { data } = await axios.post(
      `${API_URL}/api/transfer-network/requests`,
      payload,
      { withCredentials: true },
    );
    const count = data?.matched_instructors || 0;
    toast.success(
      count > 0
        ? `We found ${count} ${count === 1 ? "instructor" : "instructors"} nearby — they'll reach out if they can help`
        : "Request saved — we'll notify instructors as soon as a good match opts in",
      { duration: 7000 },
    );
    setShowForm(false);
    load();
  };

  const handleCancel = async (req) => {
    if (!window.confirm(`Cancel this transfer request? You can start a new one any time.`)) return;
    try {
      await axios.post(
        `${API_URL}/api/transfer-network/requests/${req.id}/cancel`,
        {},
        { withCredentials: true },
      );
      toast.success("Request cancelled");
      load();
    } catch {
      toast.error("Couldn't cancel — try again");
    }
  };

  const handleApprove = async (req) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/transfer-network/requests/${req.id}/learner-approve`,
        {},
        { withCredentials: true },
      );
      const count = data?.matched_instructors || 0;
      toast.success(
        count > 0
          ? `Approved — ${count} ${count === 1 ? "instructor" : "instructors"} nearby will see your opportunity`
          : "Approved — we'll let instructors know as they opt in",
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't approve");
    }
  };

  const handleDecline = async (req) => {
    if (!window.confirm("Decline this suggestion? Nothing will change and you'll stay with your current instructor.")) return;
    try {
      await axios.post(
        `${API_URL}/api/transfer-network/requests/${req.id}/learner-decline`,
        {},
        { withCredentials: true },
      );
      toast.success("Declined — nothing changes");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't decline");
    }
  };

  const openHub = (req) => {
    navigate(`/learn/transfer-hub/${req.id}`);
  };

  return (
    <div data-testid="progress-transfer-page" className="min-h-dvh no-overflow-x bg-slate-50">
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
          <button
            type="button"
            onClick={() => goBack()}
            className="p-2 rounded-xl hover:bg-slate-100"
            data-testid="progress-transfer-back"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-slate-900">Progress Transfer</h1>
            <p className="text-[11px] text-slate-500">Move instructors without losing what you've learned</p>
          </div>
          {!hasOpen && !showForm && (
            <button
              type="button"
              onClick={() => setShowForm(true)}
              data-testid="progress-transfer-start"
              className="inline-flex items-center gap-1.5 text-xs font-bold bg-indigo-600 text-white rounded-lg px-3 py-2 hover:bg-indigo-700"
            >
              <Plus className="w-3.5 h-3.5" />
              Start
            </button>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-5 space-y-4">
        {/* Calm explainer card — only show when nothing's going on */}
        {requests.length === 0 && !showForm && (
          <div className="bg-gradient-to-br from-indigo-50 to-violet-50 border border-indigo-100 rounded-2xl p-5">
            <Compass className="w-8 h-8 text-indigo-600 mb-2" />
            <h2 className="text-base font-bold text-slate-900">
              Keep your momentum, wherever life takes you
            </h2>
            <p className="text-sm text-slate-600 mt-1 leading-relaxed">
              Moved area? Need different hours? Want a fresh teaching style? Start a
              Progress Transfer and your hours, skills and confidence come with you to
              your next instructor. You always have final choice.
            </p>
            <button
              type="button"
              onClick={() => setShowForm(true)}
              data-testid="progress-transfer-empty-start"
              className="mt-3 inline-flex items-center gap-1.5 bg-indigo-600 text-white text-sm font-bold rounded-xl px-4 py-2.5 hover:bg-indigo-700"
            >
              <Plus className="w-4 h-4" />
              Start Progress Transfer
            </button>
          </div>
        )}

        {showForm && (
          <NewRequestForm
            onSave={handleCreate}
            onClose={() => setShowForm(false)}
          />
        )}

        {loading ? (
          <p className="text-sm text-slate-400 text-center py-8">Loading…</p>
        ) : (
          requests.map((r) => (
            <RequestCard
              key={r.id}
              req={r}
              onCancel={handleCancel}
              onApprove={handleApprove}
              onDecline={handleDecline}
              onOpenHub={openHub}
            />
          ))
        )}
      </div>
    </div>
  );
}
